import { RUN_KINDS, RUN_STATUSES } from "./agentTaskTypes";

export const runCountsTowardPlanQuota = (run) =>
  run && run.run_kind === RUN_KINDS.LIVE;

export const runKindLabel = (run) => {
  const k = run?.run_kind;
  if (k === RUN_KINDS.LIVE) return "Live";
  if (k === RUN_KINDS.PREVIEW) return "Preview";
  if (k === RUN_KINDS.MANUAL_TEST) return "Test run";
  if (k === RUN_KINDS.SANDBOX) return "Sample";
  return "Unknown";
};

/** Human-readable status for chips / filters. */
export const runStatusLabel = (status) => {
  const s = String(status || "").trim().toLowerCase();
  if (s === RUN_STATUSES.PARTIAL_SUCCESS || s === "partial") return "partial success";
  if (s === RUN_STATUSES.SUCCESS) return "success";
  if (s === RUN_STATUSES.SKIPPED || s === "filtered" || s === "criteria_not_met") return "skipped";
  if (s === RUN_STATUSES.HALTED || s === "halt" || s === "no_progress" || s === "stuck") {
    return "halted";
  }
  if (s === RUN_STATUSES.ERROR) return "error";
  if (s === RUN_STATUSES.RUNNING) return "running";
  if (s === RUN_STATUSES.CANCELLED || s === "canceled") return "cancelled";
  return s || "—";
};

/**
 * MUI Chip props for run status. Partial success uses amber (less alarming than error red).
 * Skipped (criteria / intentional stop) uses muted blue-grey — not a failure.
 * Halted (no-progress breaker) uses purple so the team can spot stuck loops quickly.
 * @returns {{ color?: string, style?: object, label: string }}
 */
export const runStatusChipProps = (status) => {
  const s = String(status || "").trim().toLowerCase();
  const label = runStatusLabel(s);
  if (s === RUN_STATUSES.SUCCESS) return { color: "primary", label };
  if (s === RUN_STATUSES.ERROR) return { color: "secondary", label };
  if (s === RUN_STATUSES.PARTIAL_SUCCESS || s === "partial") {
    return {
      color: "default",
      label,
      style: { backgroundColor: "#ed6c02", color: "#fff" },
    };
  }
  if (s === RUN_STATUSES.SKIPPED || s === "filtered" || s === "criteria_not_met") {
    return {
      color: "default",
      label,
      style: { backgroundColor: "#546e7a", color: "#fff" },
    };
  }
  if (s === RUN_STATUSES.HALTED || s === "halt" || s === "no_progress" || s === "stuck") {
    return {
      color: "default",
      label,
      style: { backgroundColor: "#6a1b9a", color: "#fff" },
    };
  }
  return { color: "default", label };
};

const _looksLikeIntentionalSkipError = (run) => {
  const err = String(run?.error || "").toLowerCase();
  const summary = String(run?.summary || "").toLowerCase();
  const steps = Array.isArray(run?.steps) ? run.steps : [];
  const reasoning = steps
    .filter((st) => st && String(st.phase || "").toLowerCase() === "reasoning")
    .map((st) => String(st.content || st.reasoning || st.text || st.title || ""))
    .join("\n")
    .toLowerCase();
  const blob = `${err}\n${summary}\n${reasoning}`;
  return /\b(not (a |an )?(medical )?referral|criteria (not|weren't|was not) met|do not (proceed|continue|process)|should not (proceed|continue)|condition (not|wasn't) (met|satisfied)|filter(ed)? out|outside (the )?scope|does not match|doesn't match|intentionally (stop|halt|skip)|no further action|not (eligible|applicable)|decline[sd]? to (process|proceed)|commercial (business )?funding|junk (mail|fax)|solicitation)\b/.test(
    blob
  );
};

/**
 * Display status for the tasks UI. Upgrades legacy "error" rows that also have
 * successful tool steps to partial_success so the badge is less alarming.
 * Also remaps intentional criteria-stops that were stored as error → skipped.
 */
export const effectiveRunStatus = (run) => {
  const s = String(run?.status || "").trim().toLowerCase();
  if (s === "partial" || s === RUN_STATUSES.PARTIAL_SUCCESS) return RUN_STATUSES.PARTIAL_SUCCESS;
  if (s === "canceled" || s === RUN_STATUSES.CANCELLED) return RUN_STATUSES.CANCELLED;
  if (s === RUN_STATUSES.SKIPPED || s === "filtered" || s === "criteria_not_met") {
    return RUN_STATUSES.SKIPPED;
  }
  if (s === RUN_STATUSES.HALTED || s === "halt" || s === "no_progress" || s === "stuck") {
    return RUN_STATUSES.HALTED;
  }
  // Circuit-breaker trips may leave halt_reason even if an older path wrote error.
  if (
    s === RUN_STATUSES.ERROR &&
    (String(run?.halt_reason || "").toLowerCase() === "no_progress" ||
      /\b(no progress|circuit breaker|halted)\b/i.test(String(run?.error || run?.summary || "")))
  ) {
    return RUN_STATUSES.HALTED;
  }
  if (
    s === RUN_STATUSES.SUCCESS ||
    s === RUN_STATUSES.RUNNING ||
    s === RUN_STATUSES.CANCELLED
  ) {
    return s;
  }
  if (s !== RUN_STATUSES.ERROR) return s || "";

  const steps = Array.isArray(run?.steps) ? run.steps : [];
  let ok = 0;
  let fail = 0;
  for (const step of steps) {
    if (!step || typeof step !== "object") continue;
    const phase = String(step.phase || "").toLowerCase();
    if (phase === "tool_call" || phase === "error") {
      if (step.success === false || phase === "error") fail += 1;
      else ok += 1;
    }
  }
  if (ok > 0) return RUN_STATUSES.PARTIAL_SUCCESS;
  // No tools ran / no tool failures — often an instructed gate, not a crash.
  if (fail === 0 && _looksLikeIntentionalSkipError(run)) {
    return RUN_STATUSES.SKIPPED;
  }
  return RUN_STATUSES.ERROR;
};

export const runSummaryDescription = (run) => {
  if (run?.summary) return String(run.summary);
  const t = run?.input_event?.type || run?.input_event?.source;
  const trig = run?.trigger_label;
  if (trig) return `Triggered by ${trig}`;
  if (t) return `Run: ${t}`;
  return "Agent run";
};

const _GENERIC_SUMMARIES = new Set([
  "run completed.",
  "run completed",
  "agent run in progress.",
  "agent run in progress",
  "completed.",
  "completed",
]);

const _firstSentence = (text, maxLen = 160) => {
  const t = String(text || "").trim();
  if (!t) return "";
  const m = t.match(/^[^.!?]+[.!?]?/);
  const one = (m ? m[0] : t).trim();
  if (one.length <= maxLen) return one;
  return `${one.slice(0, Math.max(0, maxLen - 1))}…`;
};

/**
 * One line for the tasks table. Prefers explicit short fields, then a non-generic summary,
 * then a sentence derived from reasoning + tool steps when the API only returns "Run completed."
 */
export const runTableDescription = (run) => {
  const explicit = run?.short_summary || run?.list_summary || run?.description;
  if (explicit != null && String(explicit).trim()) {
    return _firstSentence(String(explicit), 200);
  }
  const raw = run?.summary != null ? String(run.summary).trim() : "";
  if (raw && !_GENERIC_SUMMARIES.has(raw.toLowerCase())) {
    return _firstSentence(raw, 200);
  }

  const steps = Array.isArray(run?.steps) ? run.steps : [];
  const tools = steps.filter((s) => s && s.phase === "tool_call");
  const reasoningStep = [...steps]
    .reverse()
    .find((s) => s && (s.phase === "reasoning" || s.phase === "thinking" || s.reasoning));
  const reasoningText = reasoningStep?.reasoning
    ? String(reasoningStep.reasoning).trim()
    : "";

  if (tools.length > 0) {
    const apps = [...new Set(tools.map((t) => t.app_label || t.app).filter(Boolean))];
    const appPart = apps.length ? ` — ${apps.slice(0, 4).join(", ")}${apps.length > 4 ? "…" : ""}` : "";
    if (reasoningText) {
      const lead = _firstSentence(reasoningText, 100);
      return `${lead}${lead && !/[.!?]$/.test(lead) ? "." : ""} ${tools.length} tool call${tools.length === 1 ? "" : "s"}${appPart}.`.trim();
    }
    return `${tools.length} tool call${tools.length === 1 ? "" : "s"} completed${appPart}.`;
  }

  return runSummaryDescription(run);
};

const _normKey = (s) => String(s || "").trim().toLowerCase();

/** Stable action_id from a persisted timeline step (receipt preferred). */
export const actionIdFromRunStep = (step) => {
  if (!step || typeof step !== "object") return "";
  const receipt =
    (step.receipt && typeof step.receipt === "object" ? step.receipt : null) ||
    (step.outcome && typeof step.outcome === "object" && step.outcome.receipt
      ? step.outcome.receipt
      : null);
  return String(
    step.action_id ||
      receipt?.action_id ||
      step.request_payload?.action_id ||
      "",
  ).trim();
};

/**
 * Match a persisted timeline step to an agent action.
 *
 * Prefer action_id (required when several tools share action_key, e.g. three
 * Workload Code runPython steps). Fall back to app + operation only when that
 * pair is unique among ``allActions``.
 *
 * @param {object} step
 * @param {object} action
 * @param {{ allActions?: object[] }} [opts]
 */
export const runStepMatchesAgentAction = (step, action, opts = {}) => {
  if (!step || !action) return false;
  const stepAid = actionIdFromRunStep(step);
  const actAid = String(action.action_id || "").trim();
  if (stepAid && actAid) return stepAid === actAid;

  const appStep = _normKey(step.app_label || step.app);
  const appAct = _normKey(action.app || action.app_label);
  if (!appStep || !appAct || appStep !== appAct) return false;
  // Prefer action_key / operation — tool_label may be the human name after the
  // timeline fix (e.g. "Generate Job Summary Text" while action_key=runPython).
  const opStep = _normKey(step.action_key || step.operation || step.tool_label);
  const opAct = _normKey(action.action_key || action.config?.action);
  if (!opStep || !opAct || opStep !== opAct) return false;

  // Ambiguous shared keys (runPython × N) must not credit the first tool only.
  const allActions = Array.isArray(opts.allActions) ? opts.allActions : null;
  if (allActions && allActions.length > 0) {
    const rivals = allActions.filter((a) => {
      if (!a?.action_id) return false;
      const aApp = _normKey(a.app || a.app_label);
      const aOp = _normKey(a.action_key || a.config?.action);
      return aApp === appAct && aOp === opAct;
    });
    if (rivals.length > 1) return false;
  }
  return true;
};

/** Resolve the agent action for a timeline step (action_id first, never wrong twin). */
export const matchAgentActionForRunStep = (step, agentActions = []) => {
  const actions = Array.isArray(agentActions) ? agentActions : [];
  if (!step || !actions.length) return null;
  const stepAid = actionIdFromRunStep(step);
  if (stepAid) {
    const byId = actions.find((a) => String(a?.action_id || "").trim() === stepAid);
    if (byId) return byId;
  }
  const hits = actions.filter(
    (a) => a?.action_id && runStepMatchesAgentAction(step, a, { allActions: actions }),
  );
  return hits.length === 1 ? hits[0] : null;
};

function _tryParseOutcomeJson(outcome) {
  if (outcome == null) return null;
  if (typeof outcome === "object") return outcome;
  if (typeof outcome === "string") {
    try {
      return JSON.parse(outcome);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * One human-readable line about a tool outcome (for recap). Best-effort on common Lambda shapes.
 * Prefers step receipts when present (no base64 in the UI summary).
 */
export const outcomeSummaryLine = (outcome, receipt) => {
  const rcpt =
    (receipt && typeof receipt === "object" ? receipt : null) ||
    (outcome && typeof outcome === "object" && !Array.isArray(outcome) ? outcome.receipt : null);
  if (rcpt && typeof rcpt === "object") {
    if (rcpt.summary) return String(rcpt.summary).slice(0, 240);
    if (rcpt.error) return `Error: ${String(rcpt.error).slice(0, 160)}`;
    if (rcpt.ok === false) return "Step failed.";
    if (Array.isArray(rcpt.ids) && rcpt.ids.length) {
      const n = rcpt.count ?? rcpt.ids.length;
      return `${rcpt.resource_type || "item"} list: ${n} id(s).`;
    }
    if (rcpt.file?.upload_ref) return `File ready — ${rcpt.file.upload_ref}`;
  }
  const o = _tryParseOutcomeJson(outcome);
  if (o == null) {
    const s = String(outcome ?? "").trim();
    if (!s) return "No outcome payload recorded.";
    return s.length > 200 ? `${s.slice(0, 197)}…` : s;
  }
  if (typeof o !== "object" || Array.isArray(o)) {
    return String(o).slice(0, 200);
  }
  if (o.summary && typeof o.summary === "string") return o.summary.slice(0, 240);
  if (o.error) return `Error: ${String(o.error).slice(0, 160)}`;
  const msg = o.message;
  if (msg && typeof msg === "object" && msg.error) return `Error: ${String(msg.error).slice(0, 160)}`;

  const wr = o.workload_response || o.workloadResponse;
  if (wr && typeof wr === "object") {
    const inner = wr.data;
    if (inner && typeof inner === "object" && !Array.isArray(inner)) {
      for (const v of Object.values(inner)) {
        if (v && typeof v === "object" && Array.isArray(v.data) && v.data.length === 0) {
          return "Nested result had an empty list — no rows for that query.";
        }
      }
    }
  }

  const data = o.data;
  if (Array.isArray(data)) {
    if (data.length === 0) return "Returned 0 rows (empty array).";
    return `Returned ${data.length} row(s).`;
  }
  if (data && typeof data === "object" && Array.isArray(data.data) && data.data.length === 0) {
    return "Returned empty nested data list.";
  }
  if (o.totalCount === 0 || o.total_count === 0) {
    return "Total count was 0 — no matching records.";
  }
  if (typeof o.totalCount === "number" && o.totalCount > 0) {
    return `totalCount: ${o.totalCount}.`;
  }
  if (typeof o.totalCount === "number" && o.totalCount === 0) {
    return "totalCount was 0.";
  }

  return "Got a response.";
};

/** Timeline/UI: prefer receipt; never show raw base64-looking strings. */
export const timelineOutcomeForDisplay = (step) => {
  if (!step || typeof step !== "object") return step?.outcome;
  if (step.receipt && typeof step.receipt === "object") {
    return {
      summary: step.receipt.summary,
      ok: step.receipt.ok,
      status_code: step.receipt.status_code,
      resource_type: step.receipt.resource_type,
      ids: step.receipt.ids,
      count: step.receipt.count,
      primary_id: step.receipt.primary_id,
      upload_ref: step.receipt.file?.upload_ref,
      error: step.receipt.error,
    };
  }
  const o = step.outcome;
  if (o && typeof o === "object" && !Array.isArray(o) && o.receipt) {
    return timelineOutcomeForDisplay({ receipt: o.receipt, outcome: o });
  }
  return o;
};

/** SMB-friendly status headline for the detail pane header. */
export const runStatusHeadline = (run) => {
  const s = effectiveRunStatus(run);
  if (s === RUN_STATUSES.SUCCESS) return "Finished successfully";
  if (s === RUN_STATUSES.PARTIAL_SUCCESS) return "Finished with some issues";
  if (s === RUN_STATUSES.SKIPPED) return "Skipped — didn't need to run";
  if (s === RUN_STATUSES.HALTED) return "Halted — stuck in a loop";
  if (s === RUN_STATUSES.ERROR) return "Stopped because something went wrong";
  if (s === RUN_STATUSES.RUNNING) return "Still running…";
  if (s === RUN_STATUSES.CANCELLED) return "Cancelled";
  return "Run details";
};

/** One plain sentence explaining the outcome for non-technical users. */
export const runStatusPlainEnglish = (run) => {
  const s = effectiveRunStatus(run);
  const desc = runTableDescription(run);
  if (s === RUN_STATUSES.SUCCESS) {
    return desc && !_GENERIC_SUMMARIES.has(String(desc).toLowerCase())
      ? desc
      : "Your agent completed its steps without errors.";
  }
  if (s === RUN_STATUSES.PARTIAL_SUCCESS) {
    const err = typeof run?.error === "string" ? run.error.trim() : "";
    return err
      ? `Some steps worked, but one failed: ${err.slice(0, 160)}${err.length > 160 ? "…" : ""}`
      : "Some steps worked, but at least one had a problem. Scroll to the red step below.";
  }
  if (s === RUN_STATUSES.SKIPPED) {
    const reason = String(run?.skip_reason || run?.error || "").trim();
    return reason
      ? `The agent decided not to continue: ${reason.slice(0, 180)}${reason.length > 180 ? "…" : ""}`
      : "Based on your instructions, the agent decided nothing needed to be done this time.";
  }
  if (s === RUN_STATUSES.HALTED) {
    const err = typeof run?.error === "string" ? run.error.trim() : "";
    return err
      ? err.slice(0, 220) + (err.length > 220 ? "…" : "")
      : "The run was halted because the agent stopped making progress (repeated planning or re-fetching without moving forward).";
  }
  if (s === RUN_STATUSES.ERROR) {
    const err = typeof run?.error === "string" ? run.error.trim() : "";
    return err
      ? err.slice(0, 220) + (err.length > 220 ? "…" : "")
      : "The run stopped early. Check the red step below for what went wrong.";
  }
  if (s === RUN_STATUSES.RUNNING) return "This run is still in progress.";
  if (s === RUN_STATUSES.CANCELLED) return "This run was cancelled before it finished.";
  return desc || "Here's what happened during this run.";
};

const _humanizeFieldName = (name) => {
  const n = String(name || "").trim();
  if (!n) return "";
  // body.foo → Foo; snake_case / camelCase → words
  const base = n.includes(".") ? n.split(".").pop() : n;
  return base
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const _formatFieldValue = (val) => {
  if (val == null || val === "") return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (typeof val === "number") return String(val);
  if (typeof val === "string") {
    const s = val.trim();
    if (/^prior_file:/i.test(s)) return "File from an earlier step";
    if (s.length > 120) return `${s.slice(0, 117)}…`;
    if (s.toLowerCase() === "let agent pick value") return "Chosen automatically";
    return s;
  }
  if (Array.isArray(val)) {
    if (!val.length) return "(empty list)";
    if (val.every((x) => x == null || typeof x !== "object")) {
      return val.slice(0, 6).map(String).join(", ") + (val.length > 6 ? ` (+${val.length - 6} more)` : "");
    }
    return `${val.length} item${val.length === 1 ? "" : "s"}`;
  }
  if (typeof val === "object") {
    const keys = Object.keys(val);
    if (keys.length <= 3) {
      try {
        return JSON.stringify(val);
      } catch {
        return "Details";
      }
    }
    return `${keys.length} fields`;
  }
  return String(val);
};

/**
 * Turn request_payload.fields into friendly {label, value} rows for SMB UI.
 */
export const humanizeRequestFields = (payload) => {
  let p = payload;
  if (typeof p === "string") {
    try {
      p = JSON.parse(p);
    } catch {
      return [];
    }
  }
  if (!p || typeof p !== "object") return [];
  const fields = Array.isArray(p.fields) ? p.fields : Array.isArray(p) ? p : null;
  if (!fields) {
    // Flat object of name→value
    return Object.entries(p)
      .filter(([k]) => !["url", "credential", "authname", "token"].includes(String(k).toLowerCase()))
      .slice(0, 24)
      .map(([k, v]) => ({ label: _humanizeFieldName(k), value: _formatFieldValue(v) }));
  }
  const rows = [];
  for (const f of fields) {
    if (!f || typeof f !== "object") continue;
    const name = f.name || f.title || f.key;
    if (!name) continue;
    if (String(name).toLowerCase() === "python_code") {
      rows.push({ label: "Custom code", value: "Configured (hidden)" });
      continue;
    }
    rows.push({ label: _humanizeFieldName(name), value: _formatFieldValue(f.value) });
  }
  return rows.slice(0, 24);
};

/** Friendly phase label for timeline story. */
export const humanPhaseLabel = (phase) => {
  const p = String(phase || "").trim().toLowerCase();
  if (p === "reasoning" || p === "thinking" || p === "thought") return "Planning";
  if (p === "tool_call") return "Action";
  if (p === "error") return "Problem";
  if (p === "system" || p === "orchestrator") return "Note";
  return p ? p.replace(/_/g, " ") : "Step";
};

/**
 * Build a chronological story list for the detail pane (planning + actions).
 * Each item: { kind, title, subtitle, ok, at, step, detailLines[] }
 */
export const buildRunStorySteps = (run, agentActions = []) => {
  const steps = Array.isArray(run?.steps) ? [...run.steps] : [];
  steps.sort((a, b) => {
    const ao = a.order ?? 0;
    const bo = b.order ?? 0;
    if (ao !== bo) return ao - bo;
    const ta = a.at ? new Date(a.at).getTime() : 0;
    const tb = b.at ? new Date(b.at).getTime() : 0;
    return ta - tb;
  });
  const actions = Array.isArray(agentActions) ? agentActions : [];

  const out = [];
  let actionNum = 0;
  for (const step of steps) {
    if (!step || typeof step !== "object") continue;
    const phase = String(step.phase || "").toLowerCase();
    if (phase === "reasoning" || phase === "thinking" || phase === "thought") {
      const text = String(step.reasoning || step.content || step.text || step.title || "").trim();
      if (!text) continue;
      out.push({
        kind: "planning",
        title: "Deciding what to do next",
        subtitle: text.length > 220 ? `${text.slice(0, 217)}…` : text,
        fullText: text,
        ok: true,
        at: step.at,
        step,
        detailLines: [],
      });
      continue;
    }
    if (phase !== "tool_call" && phase !== "error") continue;
    actionNum += 1;
    const a = matchAgentActionForRunStep(step, actions);
    const app = (a?.app_label || a?.app || step.app_label || step.app || "").toString().trim();
    // Prefer human tool names — never surface action_id in the timeline title.
    // action_id is only used internally to match the correct configured tool.
    const receiptLabel = String(step.receipt?.label || "").trim();
    const collapsedTitle = [step.app_label || step.app, step.tool_label || step.operation]
      .filter(Boolean)
      .join(" — ");
    const fullLabel = String(a?.label || "").trim();
    const actionOnly = String(
      a?.action_label || step.action_label || "",
    ).trim();
    let title;
    let tool;
    if (fullLabel) {
      // Configured label is already "Workload Code: Generate Job Summary Text"
      title = fullLabel;
      tool = actionOnly || fullLabel;
    } else if (actionOnly) {
      tool = actionOnly;
      title = app ? `${app}: ${tool}` : tool;
    } else {
      tool =
        (receiptLabel && receiptLabel !== collapsedTitle ? receiptLabel : "") ||
        [step.tool_label, step.operation].filter(Boolean).join(" ").trim() ||
        (step.title || "").toString().trim() ||
        "Action";
      title =
        tool.toLowerCase().startsWith(String(app).toLowerCase() + ":") || !app
          ? tool
          : `${app}: ${tool}`;
    }
    const ok = !(phase === "error" || step.success === false);
    const whatHappened = outcomeSummaryLine(step.outcome, step.receipt);
    const fieldRows = humanizeRequestFields(step.request_payload);
    out.push({
      kind: "action",
      actionNum,
      title,
      app,
      tool,
      subtitle: whatHappened,
      ok,
      at: step.at,
      step,
      detailLines: fieldRows,
      errorDetail: !ok
        ? String(step.detail || step.receipt?.error || (typeof step.outcome?.error === "string" ? step.outcome.error : "") || "").trim()
        : "",
    });
  }
  return out;
};

/** Counts for the summary strip. */
export const runStoryCounts = (storySteps) => {
  const actions = (storySteps || []).filter((s) => s.kind === "action");
  const ok = actions.filter((s) => s.ok).length;
  const fail = actions.filter((s) => !s.ok).length;
  return { actions: actions.length, ok, fail, planning: (storySteps || []).filter((s) => s.kind === "planning").length };
};

/**
 * Rich recap from steps + optional agent definition. Prefer server LLM recap via run.recap / run.run_recap when set.
 * @param {object} run
 * @param {object[]} [agentActions] — agent.actions from GET /agents/:id so we can list tools not invoked this run.
 */
export const buildStructuredRunRecap = (run, agentActions = []) => {
  const steps = Array.isArray(run?.steps) ? [...run.steps] : [];
  steps.sort((a, b) => {
    const ao = a.order ?? 0;
    const bo = b.order ?? 0;
    if (ao !== bo) return ao - bo;
    const ta = a.at ? new Date(a.at).getTime() : 0;
    const tb = b.at ? new Date(b.at).getTime() : 0;
    return ta - tb;
  });

  const reasoningSteps = steps.filter(
    (s) => s && (s.phase === "reasoning" || s.phase === "thinking" || s.phase === "thought"),
  );
  const toolSteps = steps.filter(
    (s) => s && (s.phase === "tool_call" || s.phase === "error"),
  );

  const lines = [];

  if (reasoningSteps.length > 0) {
    const chunks = reasoningSteps
      .map((s) => (s.reasoning ? String(s.reasoning).trim() : ""))
      .filter(Boolean);
    if (chunks.length) {
      lines.push("What the agent recorded as planning / reasoning");
      lines.push(chunks.join("\n\n").slice(0, 6000));
      lines.push("");
    }
  }

  const actions = Array.isArray(agentActions) ? agentActions : [];
  if (toolSteps.length > 0) {
    lines.push("Actions executed in this run");
    toolSteps.forEach((s, i) => {
      const matched = matchAgentActionForRunStep(s, actions);
      const label =
        (matched?.label || matched?.action_label || "").toString().trim() ||
        [s.app_label || s.app, s.tool_label].filter(Boolean).join(" — ") ||
        s.title ||
        "Tool";
      const when = s.at ? new Date(s.at).toLocaleString() : "";
      const status = s.phase === "error" ? " (failed)" : s.success === false ? " (failed)" : "";
      const hint = outcomeSummaryLine(s.outcome, s.receipt);
      lines.push(
        `${i + 1}. ${label}${status}${when ? ` — ${when}` : ""}`,
        `   → ${hint}`,
      );
    });
    lines.push("");
  }

  if (actions.length > 0) {
    const matchedIds = new Set();
    for (const step of toolSteps) {
      const matched = matchAgentActionForRunStep(step, actions);
      if (matched?.action_id) matchedIds.add(matched.action_id);
      else {
        const stepAid = actionIdFromRunStep(step);
        if (stepAid) matchedIds.add(stepAid);
      }
    }
    const notRun = actions.filter((a) => a?.action_id && !matchedIds.has(a.action_id));
    if (notRun.length > 0) {
      lines.push("Configured tools not invoked in this run");
      lines.push(
        "These are on the agent definition but had no matching executeAction step in this timeline. The orchestrator or rules engine may have skipped them because conditions were not met, the run ended earlier, or only a subset of tools was scheduled for this tick.",
      );
      notRun.forEach((a) => {
        const name = a.label || `${a.app_label || a.app || "Tool"} — ${a.action_label || a.action_key || ""}`;
        lines.push(`• ${name.trim() || a.action_id}`);
      });
      lines.push("");
    }
  }

  if (lines.length === 0) {
    return "";
  }

  return lines.join("\n").trim();
};

/**
 * Closing narrative for the detail drawer (after the timeline).
 * Optional API fields: recap, run_recap (e.g. LLM-written summary from the orchestrator).
 * @param {object} run
 * @param {object[]} [agentActions]
 */
export const runTimelineRecap = (run, agentActions = []) => {
  const explicit = run?.recap || run?.run_recap;
  if (explicit != null && String(explicit).trim()) {
    return String(explicit).trim();
  }
  const raw = run?.summary != null ? String(run.summary).trim() : "";
  if (raw && !_GENERIC_SUMMARIES.has(raw.toLowerCase())) {
    return raw;
  }

  const structured = buildStructuredRunRecap(run, agentActions);
  if (String(structured || "").trim()) {
    return structured;
  }

  const steps = Array.isArray(run?.steps) ? run.steps : [];
  const tools = steps.filter((s) => s && s.phase === "tool_call");
  if (tools.length === 0) {
    return raw || "";
  }

  const apps = [...new Set(tools.map((t) => t.app_label || t.app).filter(Boolean))];
  const appList = apps.length ? apps.join(", ") : "connected integrations";
  const st = effectiveRunStatus(run);
  const outcome =
    st === "success"
      ? "finished successfully"
      : st === "partial_success"
        ? "finished with partial success"
        : st === "skipped"
          ? "was skipped because criteria were not met"
          : st === "halted"
            ? "was halted after getting stuck with no progress"
            : st === "error"
              ? "ended with an error"
              : st === "running"
                ? "is still in progress"
                : "finished";

  return `This run ${outcome} after ${tools.length} tool call${tools.length === 1 ? "" : "s"} (${appList}). Review the timeline above for request and response details.`;
};

export const runActionCount = (run) => {
  const stored = Number(run?.quota_tasks);
  if (Number.isFinite(stored) && stored > 0) return stored;
  if (Number.isFinite(run?.action_count) && run.action_count > 0) return run.action_count;
  const steps = run?.steps || [];
  const toolSteps = steps.filter(
    (s) => s && (s.phase === "tool_call" || s.phase === "error"),
  );
  if (toolSteps.length) return toolSteps.length;
  const taken = (run?.actions_taken || []).length;
  if (taken) return taken;
  const results = Number(run?.results_count);
  if (Number.isFinite(results) && results > 0) return results;
  return 0;
};

/** App slug/label from a persisted run step (backend: append_execute_action_step). */
function appLabelFromStep(step) {
  if (!step || typeof step !== "object") return "";
  let label = (step.app_label || step.app || "").toString().trim();
  if (label) return label;
  const title = (step.title || "").toString();
  const sep = " — ";
  const i = title.indexOf(sep);
  if (i > 0) return title.slice(0, i).trim();
  return "";
}

/**
 * Distinct app icons for table avatars: run.app_icons (if any usable), actions_taken + agent actions,
 * then steps (tool_call / error executeAction rows — same source as runTableDescription).
 *
 * Live runs often have steps with app_label ("Date Format") + action_id but no actions_taken /
 * app_icons. Index agent actions by action_id, app slug, and app_label so icons still resolve.
 */
export function runAppIcons(run, agentActions = []) {
  if (Array.isArray(run?.app_icons) && run.app_icons.length) {
    const withUrls = run.app_icons.filter((x) => x && (x.icon_url || x.url));
    if (withUrls.length) return withUrls;
  }

  const byId = {};
  const bySlug = {};
  const registerSlug = (raw, action) => {
    const key = (raw || "").toString().trim().toLowerCase();
    if (!key || !action) return;
    bySlug[key] = action;
    const compact = key.replace(/[\s_-]+/g, "");
    if (compact && compact !== key) bySlug[compact] = action;
  };
  (agentActions || []).forEach((a) => {
    if (a?.action_id) byId[a.action_id] = a;
    registerSlug(a?.app, a);
    registerSlug(a?.app_label, a);
  });

  const seen = new Set();
  const out = [];

  const pushIcon = ({ app, url, dedupeKey }) => {
    const label = (app || "").toString().trim();
    const iconUrl = (url || "").toString().trim();
    if (!iconUrl && !label) return;
    const key = (dedupeKey || iconUrl || label).toString().trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    // Also reserve the display label so steps and actions_taken don't double-count.
    if (label) seen.add(label.toLowerCase());
    out.push({ icon_url: iconUrl, app: label || "app" });
  };

  const resolveFromLabel = (rawLabel) => {
    const app = (rawLabel || "").toString().trim();
    if (!app) return null;
    const key = app.toLowerCase();
    return bySlug[key] || bySlug[key.replace(/[\s_-]+/g, "")] || null;
  };

  for (const taken of run?.actions_taken || []) {
    const a = byId[taken.action_id] || taken;
    const url = a?.app_icon || taken?.app_icon;
    const app = (a?.app_label || a?.app || taken?.app_label || taken?.app || "").toString().trim();
    pushIcon({
      app,
      url,
      dedupeKey: a?.app || taken?.app || app,
    });
  }

  const steps = Array.isArray(run?.steps) ? run.steps : [];
  for (const s of steps) {
    if (!s || (s.phase !== "tool_call" && s.phase !== "error")) continue;
    const isToolLike =
      s.request_summary ||
      (s.request_payload && typeof s.request_payload === "object") ||
      (s.title && String(s.title).includes(" — "));
    if (!isToolLike) continue;

    const fromId = s.action_id ? byId[s.action_id] : null;
    const label = appLabelFromStep(s) || fromId?.app_label || fromId?.app || "";
    const fromLabel = resolveFromLabel(label) || resolveFromLabel(s.app);
    const action = fromId || fromLabel;
    pushIcon({
      app: action?.app_label || action?.app || label,
      url: action?.app_icon || s.app_icon || "",
      dedupeKey: action?.app || s.app || label,
    });
  }

  return out;
}

export const formatRunWhen = (run) => {
  if (!run?.created_at) return "—";
  try {
    return new Date(run.created_at).toLocaleString();
  } catch {
    return String(run.created_at);
  }
};

/** Human-readable run duration from latency_ms (e.g. 30480 → "30s", 120813 → "2m 1s"). */
export const formatDurationMs = (ms) => {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return "";
  const totalSec = Math.max(1, Math.round(n / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
};
